import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import quickSightLogo from '../../assets/QuickSightLogo.png';
import { AthleteAvatar } from '../components/athlete_avatar';
import { BroadJumpScatterChart } from '../components/broad_jump_scatter_chart';
import { PageSection } from '../components/page_section';
import { SprintLineChart } from '../components/sprint_line_chart';
import { StatusPanel } from '../components/status_panel';
import { publicApi } from '../data/api';
import { waitForMinimumLoadingTime } from '../lib/minimum_loading_delay';
import {
  formatHeight,
  formatMetric,
  getDisplayName,
  getShortAthleteId,
  metricDefinitions,
  primaryMetricKeys,
} from '../lib/result_utils';
import type { AthleteProfile, PrimaryMetricKey, PublicResult } from '../types/result';

import FortyYardDashIcon from '../../assets/icons/40-yard.png';
import BroadJumpIcon from '../../assets/icons/broad-jump.png';
import ConeDrillIcon from '../../assets/icons/cone-drill.png';
import VerticalJumpIcon from '../../assets/icons/vertical-jump.png';

type LoadState = 'loading' | 'ready' | 'not_found' | 'error';

const summaryMetrics: Array<{ key: PrimaryMetricKey | 'rank'; label: string }> = [
  { key: 'rank', label: 'Rank' },
  { key: 'sprint40Seconds', label: '40 Yard Dash' },
  { key: 'broadJumpInches', label: 'Broad Jump' },
  { key: 'verticalJumpInches', label: 'Vertical Jump' },
  { key: 'proAgilitySeconds', label: 'Pro Agility' },
];

function benchmarkName(result: PublicResult): string {
  return result.benchmarkLabel ?? getDisplayName(result);
}

function metricIcon(key: PrimaryMetricKey): string | null {
  if (key === 'sprint40Seconds') {
    return FortyYardDashIcon;
  }
  if (key === 'broadJumpInches') {
    return BroadJumpIcon;
  }
  if (key === 'proAgilitySeconds') {
    return ConeDrillIcon;
  }
  if (key === 'verticalJumpInches') {
    return VerticalJumpIcon;
  }
  return null;
}

const loadingTexts = [
  'Crunching combine metrics...',
  'Finding your leaderboard spot...',
  'Warming up your benchmark matchup...',

];

function ProfileLoading() {
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setLoadingTextIndex((index) => (index + 1) % loadingTexts.length);
    }, 650);
    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div
      className="hero-gradient flex min-h-[calc(100dvh-3.5rem)] flex-col items-center justify-center overflow-hidden px-8 text-center text-white"
      role="status"
      aria-label="Loading athlete profile"
    >
      <div className="profile-loader-logo relative grid size-24 place-items-center rounded-3xl border border-white/20 bg-white/10 p-3 backdrop-blur">
        <img src={quickSightLogo} alt="" className="size-full object-contain" />
      </div>

      <p className="mt-8 text-xs font-black uppercase tracking-[0.3em] text-cyan-300">
        Patriots Creator Combine presented by AWS
      </p>
      <h1 className="mt-3 text-2xl font-black">Loading your performance</h1>
      <p className="mt-2 text-sm text-indigo-100">Turning event-day numbers into your athlete story.</p>

      <div className="mt-8 h-2 w-full max-w-72 overflow-hidden rounded-full bg-white/15" aria-hidden="true">
        <div className="profile-loader-bar h-full w-2/5 rounded-full bg-cyan-300" />
      </div>

      <div className="mt-7 space-y-3 text-xs font-bold text-indigo-100" aria-hidden="true">
        {loadingTexts[loadingTextIndex]}
      </div>
      <span className="sr-only">Please wait while your results and benchmarks are prepared.</span>
    </div>
  );
}

export function AthletePage() {
  const { athleteId = '' } = useParams();
  return <AthletePageContent key={athleteId} athleteId={athleteId} />;
}

function AthletePageContent({ athleteId }: { athleteId: string }) {
  const [profile, setProfile] = useState<AthleteProfile | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    const controller = new AbortController();

    void Promise.allSettled([
      publicApi.getAthleteProfile(athleteId),
      waitForMinimumLoadingTime(2000, controller.signal),
    ]).then(([profileResult]) => {
        if (!isCurrent) return;
        if (profileResult.status === 'rejected') {
          setLoadState('error');
          return;
        }
        const nextProfile = profileResult.value;
        if (!nextProfile) {
          setLoadState('not_found');
          return;
        }
        setProfile(nextProfile);
        setLoadState('ready');
        document.title = `${getDisplayName(nextProfile.athlete)} | Patriots Creator Combine`;
      });

    return () => {
      isCurrent = false;
      controller.abort();
      document.title = 'Patriots Creator Combine';
    };
  }, [athleteId, retryKey]);

  if (loadState === 'loading') {
    return <ProfileLoading />;
  }

  if (loadState === 'error') {
    return (
      <PageSection className="app-gradient min-h-[calc(100dvh-3.5rem)] py-8">
        <StatusPanel
          title="We could not load this profile"
          tone="error"
          action={<button type="button" onClick={() => { setLoadState('loading'); setRetryKey((value) => value + 1); }} className="button-primary px-5 py-3 font-bold">Try again</button>}
        >
          The results service may be temporarily unavailable.
        </StatusPanel>
      </PageSection>
    );
  }

  if (loadState === 'not_found' || !profile) {
    return (
      <PageSection className="app-gradient min-h-[calc(100dvh-3.5rem)] py-8">
        <StatusPanel title="Athlete not found" action={<Link to="/" className="font-black underline">Search another ID</Link>}>
          No public result matches this athlete ID.
        </StatusPanel>
      </PageSection>
    );
  }

  const { athlete, professionalBenchmarks } = profile;
  const columns = [athlete, ...professionalBenchmarks];

  return (
    <div className="app-gradient min-h-[calc(100dvh-3.5rem)] px-4 py-8 sm:px-6">
      <header className="surface-card flex items-center gap-4 p-5">
        <AthleteAvatar firstName={athlete.firstName} lastName={athlete.lastName} imageUrl={athlete.fullSizeImageUrl} size="md" />
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-black">{getDisplayName(athlete)}</h1>
          <p className="mt-1 text-[11px] font-bold text-aws-slate">
            Height: {formatHeight(athlete.heightInches)} · Weight: {athlete.weightPounds ? `${athlete.weightPounds} lb` : '-'}
          </p>
          <Link to={`/leaderboard?athleteId=${encodeURIComponent(getShortAthleteId(athlete.externalId))}`} className="button-primary mt-2 inline-block px-3 py-1.5 text-xs font-black">
            View Leaderboard
          </Link>
        </div>
      </header>
      <div className="mt-7 grid grid-cols-1 gap-7 sm:grid-cols-2">
        <section className="surface-card grid grid-cols-3 gap-x-3 gap-y-7 px-4 py-7 text-center" aria-label="Athlete score summary">
          {summaryMetrics.map(({ key, label }) => {
            if (key !== 'rank' && athlete[key] === null) {
              return null;
            }
            return (
            <div key={key} className="flex flex-col items-center justify-center">
              <p className="text-[10px] font-bold text-aws-slate">{label}</p>
              <p className="mt-1 text-lg font-black text-qs-indigo">
                {key === 'rank'
                  ? profile.rank === null ? '-' : `#${profile.rank}`
                  : formatMetric(athlete[key], key)}
              </p>
            </div>
            );
          }
          )}
        </section>

        <section className="surface-card px-3 py-5" aria-labelledby="comparison-title">
          <h2 id="comparison-title" className="sr-only">Professional benchmark comparison</h2>
          <table className="w-full table-fixed text-center">
            <caption className="sr-only">Athlete scores compared with two professional benchmarks</caption>
            <colgroup>
              <col className="w-10" />
              <col />
              <col />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col"><span className="sr-only">Metric</span></th>
                {columns.map((result, index) => (
                  <th key={result.externalId} scope="col" className="px-1 pb-4 align-top">
                      <AthleteAvatar
                        firstName={result.firstName}
                        lastName={result.lastName}
                        imageUrl={result.thumbnailImageUrl}
                        size="sm"
                        className="mx-auto !size-12 !text-sm !ring-0 !shadow-none"
                      />
                      <span className="mt-2 block truncate text-[9px] font-black" title={index === 0 ? getDisplayName(result) : benchmarkName(result)}>
                      {index === 0 ? 'Your scores' : benchmarkName(result)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {primaryMetricKeys.map((key) => (
                <tr key={key} className="border-b border-indigo-100 last:border-0">
                  <th scope="row" className="py-2 pr-2">
                    {metricIcon(key) ? (
                      <img
                        src={metricIcon(key) ?? undefined}
                        alt={metricDefinitions[key].label}
                        className="mx-auto h-6 w-6"
                      />
                    ) : (
                      <span
                        role="img"
                        aria-label={metricDefinitions[key].label}
                        className="mx-auto grid h-6 w-6 place-items-center text-[9px] font-black"
                      >
                        VJ
                      </span>
                    )}
                  </th>
                  {columns.map((result) => (
                    <td key={result.externalId} className="px-1 py-2 text-xs font-black">
                      {formatMetric(result[key], key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="surface-card p-5" aria-labelledby="sprint-chart-heading">
          <h2 id="sprint-chart-heading" className="text-xl font-black">40 Yard Dash Comparison</h2>
          <p className="mt-1 text-sm text-aws-slate">
            Compare your progress through each distance milestone with the participant average and
            pro athletes.
          </p>
          <div className="mt-4">
            <SprintLineChart
              data={profile.sprintChart}
              professionalBenchmarks={professionalBenchmarks}
            />
          </div>
        </section>

        <section className="surface-card p-5" aria-labelledby="broad-jump-title">
          <h2 id="broad-jump-title" className="text-xl font-black">Broad Jump Comparison</h2>
          <p className="mt-1 text-sm text-aws-slate">
            See how jump distance compares across participant and pro athlete heights.
          </p>
          <div className="mt-4">
            <BroadJumpScatterChart
              points={profile.broadJumpScatterPoints}
              professionalBenchmarks={professionalBenchmarks}
            />
          </div>
        </section>

        <section className="surface-card p-5" aria-labelledby="social-title">
          <h2 id="social-title" className="text-xl font-black">Share on Social Media!</h2>
          <div className="mt-5 grid grid-cols-2 items-end gap-5 text-center">
            <div>
              <img src={athlete.socialCard4x5Url || 'https://placehold.co/400x500'} alt="Feed social card preview" className="mx-auto w-full max-w-[200px] rounded-xl border border-indigo-200 shadow-md" />
              <p className="mt-2 text-xs">Feed Post</p>
              {athlete.socialCard4x5DownloadUrl ? (
                <a href={athlete.socialCard4x5DownloadUrl} role="button" className="button-primary mt-3 inline-block px-3 py-2 text-xs font-black">
                  Download Feed
                </a>
              ) : (
                <button type="button" disabled className="button-primary mt-3 px-3 py-2 text-xs font-black opacity-50">
                  Feed Unavailable
                </button>
              )}
            </div>
            <div>
              <img src={athlete.socialCard9x16Url || 'https://placehold.co/1080x1920'} alt="Story social card preview" className="mx-auto w-full max-w-[200px] rounded-xl border border-indigo-200 shadow-md" />
              <p className="mt-2 text-xs">Story Post</p>
              {athlete.socialCard9x16DownloadUrl ? (
                <a href={athlete.socialCard9x16DownloadUrl} role="button" className="button-primary mt-3 inline-block px-3 py-2 text-xs font-black">
                  Download Story
                </a>
              ) : (
                <button type="button" disabled className="button-primary mt-3 px-3 py-2 text-xs font-black opacity-50">
                  Story Unavailable
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
